import {put, call} from 'redux-saga/effects';
import BankAccount from "../../entities/bankAccount";
import * as Actions from "../actions/bankAccountActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type BankAccountAction = {
    bankAccountId: string;
    bankAccount: BankAccount;
    bankAccounts: BankAccount[];
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneBankAccount(action: BankAccountAction) {
	const session = tabSession.captureGeneration();
	try {
		const bankAccount: BankAccount = yield call(api.findOne, 'bankAccount', action.bankAccountId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.BANKACCOUNT_FETCH_ONE_SUCCESS, bankAccountId: action.bankAccountId, bankAccount});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.BANKACCOUNT_FETCH_ONE_FAILURE, bankAccountId: action.bankAccountId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {BankAccountAction} action
 */
export function* saveBankAccount(action: BankAccountAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ BANKACCOUNT_SAVE saga', action.bankAccount?.id);
    
	if (action.bankAccount === undefined || action.bankAccount === null) {
		console.error('saveBankAccount received a null');
		throw new Error('saveBankAccount received a null');
	}

	if (!(action.bankAccount instanceof BankAccount)) {
		action.bankAccount = new BankAccount(action.bankAccount);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'bankAccount', action.bankAccount);
		if (addResult.isErr()) {
			console.error(`⮂ saveBankAccount: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.BANKACCOUNT_SAVE_FAILURE, bankAccount: action.bankAccount, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.BANKACCOUNT_SAVE, bankAccount: action.bankAccount});

		// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
		// removes it when it saves; a draft typed somewhere else may be newer than this copy.
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.BANKACCOUNT_SAVE_FAILURE, bankAccount: action.bankAccount, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {BankAccountAction} action
 * @param {BankAccount[]} action.bankAccounts
 */
export function* saveManyBankAccounts(action: BankAccountAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    // console.debug('⮂ BANKACCOUNT_SAVE_MANY saga', action.bankAccounts.map(bankAccount => bankAccount.id).join(','));
    
    if (!Array.isArray(action.bankAccounts)) {
        console.error('saveManyBankAccounts expected an array, received', action.bankAccounts);
        throw new Error('saveManyBankAccounts expected an array');
    }

	try {
		const toSave: BankAccount[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.bankAccounts.length; i++) {
			const bankAccount = action.bankAccounts[i];
			bankAccount.persisted = false;
			toSave.push(bankAccount);
		}

		const addResult = yield call(idb.add, 'bankAccount', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyBankAccounts: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			session.assertCurrent();
			yield put({type: Actions.BANKACCOUNT_SAVE_MANY_FAILURE, bankAccounts: action.bankAccounts, status: 500, message: addResult.unwrapErr().message});
			return;
		}
		reportQueued(true);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.BANKACCOUNT_SAVE_MANY, bankAccounts: toSave});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) { reportQueued(false); return; }
		reportQueued(false);
		yield put({type: Actions.BANKACCOUNT_SAVE_MANY_FAILURE, bankAccounts: action.bankAccounts, status: 500, err});

		console.error(`Error saving ${action.bankAccounts.length} bankAccounts to database`, err);
	}
}

/**
 * @param {BankAccountAction} action
 */
export function* removeBankAccount(action: BankAccountAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
	if (!userId || action.bankAccount === undefined || action.bankAccount === null || !(action.bankAccount instanceof BankAccount)) {
			console.error('removeBankAccount expected type BankAccount, received', action.bankAccount);
			throw new Error('removeBankAccount expected type BankAccount');
		}

	try {
		// Mark as deleted
		action.bankAccount.status = 'D';
		action.bankAccount.addedOn = newHRTimestamp();
		action.bankAccount.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'bankAccount', action.bankAccount);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeBankAccount: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.BANKACCOUNT_REMOVE_FAILURE, bankAccount: action.bankAccount, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.BANKACCOUNT_REMOVE, bankAccount: action.bankAccount});
		session.assertCurrent();

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.BANKACCOUNT_REMOVE_FAILURE, bankAccount: action.bankAccount, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this BankAccount.
	*
	* @param {BankAccountAction} action
	*/
export function* restoreBankAccount(action: BankAccountAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreBankAccount expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.bankAccount === undefined || action.bankAccount === null) {
        console.error('restoreBankAccount expected a BankAccount, received null');
        throw new Error('restoreBankAccount expected a BankAccount, received null');
    }
    else if (!(action.bankAccount instanceof BankAccount)) action.bankAccount = new BankAccount(action.bankAccount); 
    
	try {
		// Mark as restored
		action.bankAccount.status = 'U';
		action.bankAccount.addedOn = newHRTimestamp();
		action.bankAccount.addedBy = userId;
		action.bankAccount.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'bankAccount', action.bankAccount);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreBankAccount: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.BANKACCOUNT_RESTORE_FAILURE, bankAccount: action.bankAccount, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.BANKACCOUNT_RESTORE, bankAccount: action.bankAccount});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.BANKACCOUNT_RESTORE_FAILURE, bankAccount: action.bankAccount, status: 500, err});

		console.error('Error restoring from database', err);
	}
}